import React, { useState } from "react"
import { Modal, Button } from "antd"
import {
	CloseOutlined,
	DownOutlined,
	SyncOutlined,
	WarningOutlined,
} from "@ant-design/icons"
import Strings from "utils/strings"
import { Route, RouteAddress } from "../../types"
import {
	ChangeType,
	OptimizePreviewData,
	PendingMove,
	ReadOnlyStop,
} from "./types"
import { deliveryStops, tint } from "./utils"
import ReadOnlyRouteRow from "./ReadOnlyRouteRow"

const BLUE = "#2563eb"
const AMBER = "#b45309"

interface OptimizePreviewModalProps {
	selectedRoutes: Route[]
	// Full fetched list — a bike route auto-updated by cascade may never have
	// been selected, so its data only exists here, not in selectedRoutes/stopsByRoute.
	allRoutes: Route[]
	stopsByRoute: Record<string, RouteAddress[]>
	sequenceByRoute: Record<string, Record<string, number>>
	pendingMoves: Map<string, PendingMove>
	routeColor: (routeId: string) => string
	optimizePreview: OptimizePreviewData | null
	confirming: boolean
	onCancel: () => void
	onConfirm: () => void
}

// Before/after comparison shown once "Otimizar Rotas" comes back. One card per
// route; routes only touched by cascade start collapsed so a van's own bikes
// don't bury the modal.
const OptimizePreviewModal: React.FC<OptimizePreviewModalProps> = ({
	selectedRoutes,
	allRoutes,
	stopsByRoute,
	sequenceByRoute,
	pendingMoves,
	routeColor,
	optimizePreview,
	confirming,
	onCancel,
	onConfirm,
}) => {
	// Only records a route once the user toggles it — the default (expanded
	// unless auto-updated) is derived fresh from optimizePreview every render.
	const [expandedOverrides, setExpandedOverrides] = useState<
		Record<string, boolean>
	>({})

	// Looked up across every route's stops — an order may currently sit
	// elsewhere than where the response lists it.
	const addrByOrderId = new Map<string, RouteAddress>()
	allRoutes.forEach((route) =>
		deliveryStops(route).forEach((a) => addrByOrderId.set(a.order.orderId, a))
	)
	Object.values(stopsByRoute).forEach((stops) =>
		stops.forEach((a) => addrByOrderId.set(a.order.orderId, a))
	)

	const findRoute = (routeId: string) =>
		selectedRoutes.find((r) => r._id === routeId) ??
		allRoutes.find((r) => r._id === routeId)

	if (!optimizePreview) return null

	// Full list (for its own row) plus an orderId lookup (for the rank diff).
	const currentStopsByRoute: Record<
		string,
		{ addr: RouteAddress; sequence: number }[]
	> = {}
	const currentByOrderId = new Map<
		string,
		{ routeId: string; sequence: number }
	>()
	optimizePreview.routes.forEach((previewRoute) => {
		const route = findRoute(previewRoute.routeId)
		if (!route) return
		// A cascaded route never selected has no entry in stopsByRoute.
		const hasLocalState = route._id in stopsByRoute
		const ordered = hasLocalState
			? [...stopsByRoute[route._id]].sort(
					(a, b) =>
						(sequenceByRoute[route._id]?.[a.order.orderId] ?? 0) -
						(sequenceByRoute[route._id]?.[b.order.orderId] ?? 0)
				)
			: deliveryStops(route)
		currentStopsByRoute[route._id] = ordered.map((addr, i) => {
			const sequence = hasLocalState
				? (sequenceByRoute[route._id]?.[addr.order.orderId] ?? 0)
				: i + 1
			currentByOrderId.set(addr.order.orderId, { routeId: route._id, sequence })
			return { addr, sequence }
		})
	})

	return (
		<Modal
			visible
			onCancel={onCancel}
			width={900}
			closable={false}
			footer={null}
			bodyStyle={{
				padding: 0,
				display: "flex",
				flexDirection: "column",
				maxHeight: "80vh",
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "18px 22px",
					borderBottom: "1px solid #eef0f2",
					flexShrink: 0,
				}}
			>
				<div style={{ fontSize: 18, fontWeight: 600, color: "#1a1d24" }}>
					{Strings.routes.moveOrdersOptimizePreviewTitle}
				</div>
				<button
					type="button"
					onClick={onCancel}
					aria-label={Strings.generic.close}
					style={{
						background: "none",
						border: "none",
						cursor: "pointer",
						padding: 4,
						display: "flex",
						borderRadius: 6,
					}}
				>
					<CloseOutlined style={{ fontSize: 14, color: "#6b7280" }} />
				</button>
			</div>

			<div
				style={{
					padding: "20px 22px",
					overflowY: "auto",
					flex: 1,
					display: "flex",
					flexDirection: "column",
					gap: 16,
				}}
			>
				{optimizePreview.skippedOrderIds.length > 0 && (
					<div
						style={{
							display: "flex",
							gap: 12,
							padding: "14px 16px",
							background: "#fdf6ec",
							border: "1px solid #f3e3bf",
							borderLeft: `3px solid ${AMBER}`,
							borderRadius: 8,
						}}
					>
						<div style={{ flexShrink: 0, paddingTop: 1 }}>
							<WarningOutlined style={{ color: AMBER, fontSize: 18 }} />
						</div>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div
								style={{
									fontSize: 14,
									fontWeight: 600,
									color: "#7c4a03",
									marginBottom: 2,
								}}
							>
								{Strings.routes.moveOrdersOptimizeSkippedWarning}
							</div>
							<div style={{ fontSize: 13, color: "#8a5a13", marginBottom: 10 }}>
								{Strings.routes.moveOrdersOptimizeSkippedWarningDetail}
							</div>
							<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
								{optimizePreview.skippedOrderIds.map((orderId) => (
									<span
										key={orderId}
										style={{
											fontFamily:
												"ui-monospace, SFMono-Regular, Menlo, monospace",
											fontSize: 12.5,
											fontWeight: 600,
											color: "#7c4a03",
											background: "#fff",
											border: "1px solid #f0dfb4",
											borderRadius: 6,
											padding: "4px 9px",
										}}
									>
										{orderId}
									</span>
								))}
							</div>
						</div>
					</div>
				)}

				<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					{optimizePreview.routes.map((previewRoute) => {
						const route = findRoute(previewRoute.routeId)
						if (!route) return null
						const color = routeColor(route._id)
						const autoUpdated = optimizePreview.autoUpdatedRouteIds.includes(
							route._id
						)
						const expanded = expandedOverrides[route._id] ?? !autoUpdated

						const optimizedStops: ReadOnlyStop[] = previewRoute.orders.flatMap(
							(o, i) => {
								const addr = addrByOrderId.get(o.orderId)
								if (!addr) return []
								const current = currentByOrderId.get(o.orderId)
								// The optimizer only reorders within a route, never reassigns
								// routes — "moved" comes from the same pendingMoves the timeline uses.
								const pendingMove = pendingMoves.get(o.orderId)
								const changeType: ChangeType = pendingMove
									? "moved"
									: current && current.sequence !== i + 1
										? "repositioned"
										: "none"
								return [
									{
										addr,
										eta: o.eta,
										sequence: i + 1,
										changeType,
										previousSequence: current?.sequence,
										previousRouteName: pendingMove
											? findRoute(pendingMove.originalRouteId)?.name
											: undefined,
										pickupEta: o.pickupEta,
									},
								]
							}
						)
						const changedCount = optimizedStops.filter(
							(s) => s.changeType !== "none"
						).length
						// Bolder header treatment for routes with a lot of changes, so
						// they stand out from routes barely touched.
						const tier =
							changedCount >= 6 ? "high" : changedCount >= 1 ? "med" : "none"

						return (
							<div
								key={route._id}
								style={{
									background: "#fff",
									border: "1px solid #e6e8eb",
									borderRadius: 10,
									overflow: "hidden",
									boxShadow:
										tier === "high" ? `0 1px 0 ${tint(color, 0.25)}` : "none",
								}}
							>
								<div
									onClick={() =>
										setExpandedOverrides((prev) => ({
											...prev,
											[route._id]: !expanded,
										}))
									}
									style={{
										display: "flex",
										alignItems: "center",
										gap: 10,
										padding: "12px 16px",
										cursor: "pointer",
										background: tier === "high" ? tint(color, 0.05) : "#fff",
									}}
								>
									<DownOutlined
										rotate={expanded ? 0 : -90}
										style={{
											fontSize: 12,
											color: "#6b7280",
											transition: "transform 0.15s ease",
											flexShrink: 0,
										}}
									/>
									<span
										style={{
											width: 4,
											height: tier === "high" ? 22 : 18,
											borderRadius: 2,
											background: color,
											flexShrink: 0,
										}}
									/>
									<div
										style={{
											display: "flex",
											alignItems: "baseline",
											gap: 8,
											flex: 1,
											minWidth: 0,
										}}
									>
										<span
											style={{
												fontSize: 14.5,
												fontWeight: 650,
												color: "#1a1d24",
											}}
										>
											{route.name}
										</span>
										<span style={{ fontSize: 12.5, color: "#a3a8b0" }}>
											{route.driverInfo?.name || "-"}
										</span>
									</div>
									{autoUpdated && (
										<span
											style={{
												display: "inline-flex",
												alignItems: "center",
												fontSize: 12,
												fontWeight: 600,
												color: AMBER,
												background: "#fef3e2",
												border: "1px solid #f6dcae",
												borderRadius: 6,
												padding: "3px 9px",
												whiteSpace: "nowrap",
											}}
										>
											<SyncOutlined style={{ marginRight: 4, fontSize: 11 }} />
											{Strings.routes.moveOrdersAutoUpdatedTag}
										</span>
									)}
									{changedCount > 0 &&
										(tier === "high" ? (
											<span
												style={{
													fontSize: 12.5,
													fontWeight: 700,
													color: "#fff",
													background: BLUE,
													borderRadius: 6,
													padding: "4px 10px",
													whiteSpace: "nowrap",
												}}
											>
												{Strings.formatString(
													Strings.routes.moveOrdersOptimizeChangedCount,
													changedCount
												)}
											</span>
										) : (
											<span
												style={{
													fontSize: 12.5,
													fontWeight: 600,
													color: BLUE,
													background: "#eaf1ff",
													border: "1px solid #cddcfb",
													borderRadius: 6,
													padding: "3px 9px",
													whiteSpace: "nowrap",
												}}
											>
												{Strings.formatString(
													Strings.routes.moveOrdersOptimizeChangedCount,
													changedCount
												)}
											</span>
										))}
								</div>

								{expanded && (
									<div style={{ borderTop: "1px solid #eef0f2" }}>
										<ReadOnlyRouteRow
											route={route}
											color={color}
											currentStops={currentStopsByRoute[route._id] || []}
											optimizedStops={optimizedStops}
										/>
									</div>
								)}
							</div>
						)
					})}
				</div>
			</div>

			<div
				style={{
					display: "flex",
					justifyContent: "flex-end",
					gap: 10,
					padding: "14px 22px",
					borderTop: "1px solid #eef0f2",
					flexShrink: 0,
				}}
			>
				<Button onClick={onCancel} style={{ borderRadius: 7 }}>
					{Strings.routes.moveOrdersOptimizeCancel}
				</Button>
				<Button
					type="primary"
					loading={confirming}
					onClick={onConfirm}
					style={{ borderRadius: 7, fontWeight: 650 }}
				>
					{Strings.routes.moveOrdersOptimizeConfirm}
				</Button>
			</div>
		</Modal>
	)
}

export default OptimizePreviewModal
